import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  GanttChartSquare, Clock, Video, BookOpen, Loader2, ShieldCheck, Award,
  Radio, GraduationCap, ArrowRight, CalendarDays, MapPin, Tag, CheckCircle2, X, Search, ShoppingBasket,
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/helpers";
import { addToBasket, useBasket } from "@/lib/basket";
import { RichText, stripHtml } from "@/components/RichText";
import { CompareMembershipsDialog } from "@/components/CompareMemberships";

export default function Shop({ initialCat }) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [courses, setCourses] = useState(null);
  const [selected, setSelected] = useState(null);
  const [interest, setInterest] = useState(null);
  const [cat, setCat] = useState(initialCat || new URLSearchParams(window.location.search).get("category") || "All");
  const [query, setQuery] = useState("");
  const [details, setDetails] = useState(null);
  const [compare, setCompare] = useState(false);
  const { count } = useBasket();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shop/courses");
        setCourses(data);
        if (initialCat || new URLSearchParams(window.location.search).get("category")) {
          setTimeout(() => document.getElementById("shop-courses")?.scrollIntoView({ behavior: "smooth" }), 300);
        }
        const want = new URLSearchParams(window.location.search).get("course");
        const c = want && data.find((x) => x.id === want);
        if (c) {
          if (c.register_interest) setInterest(c); else setSelected(c);
          setTimeout(() => document.querySelector(`[data-testid="shop-course-${c.id}"]`)?.scrollIntoView({ block: "center" }), 300);
        }
      }
      catch { setCourses([]); }
    })();
  }, []);

  const SHOP_CATS = ["All", "Live CPD Training", "E-learning", "Safeguarding", "SEND",
    "First Aid", "HR, Compliance and Consultancy", "Training Programmes", "Memberships"];
  const cats = (c) => (c.categories?.length ? c.categories : [c.category]);
  const visible = !courses ? [] : courses.filter((c) =>
    (cat === "All" || cats(c).includes(cat)) &&
    (c.title.toLowerCase().includes(query.toLowerCase()) ||
     (stripHtml(c.description) || "").toLowerCase().includes(query.toLowerCase()) ||
     cats(c).join(" ").toLowerCase().includes(query.toLowerCase())));

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="h-16 sticky top-0 z-20 backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-b border-border">
        <div className="max-w-6xl mx-auto h-full flex items-center px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center overflow-hidden">{branding.logo_url ? <img src={branding.logo_url} alt="logo" className="h-full w-full object-contain" /> : <GanttChartSquare className="h-5 w-5 text-white" />}</div>
            <span className="font-heading font-bold text-lg">{branding.platform_name}</span>
          </div>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => navigate("/basket")} data-testid="shop-basket-button" className="relative">
            <ShoppingBasket className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Basket</span>
            {count > 0 && <span className="ml-1 rounded-full bg-primary text-white text-xs px-1.5 min-w-[1.25rem]" data-testid="shop-basket-count">{count}</span>}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/members")} data-testid="shop-members-button">Member area</Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/login")} data-testid="shop-signin-button">Staff sign in</Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-slate-900">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 15% 25%, #2563EB 0, transparent 40%), radial-gradient(circle at 85% 75%, #10B981 0, transparent 40%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Badge className="bg-white/10 text-white border-white/20 hover:bg-white/10 mb-5">Accredited CPD &amp; Live Training</Badge>
          <h1 className="font-heading text-3xl sm:text-5xl font-bold text-white tracking-tight leading-tight max-w-2xl">
            {branding.shop_hero_title}
          </h1>
          <p className="text-slate-300 mt-4 text-base max-w-xl leading-relaxed">
            {branding.shop_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Catalog */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 id="shop-courses" className="font-heading text-2xl font-bold tracking-tight scroll-mt-20">Available Courses</h2>
            <p className="text-sm text-muted-foreground mt-1">Secure checkout · access link emailed on payment</p>
          </div>
        </div>

        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search courses by name or topic…" className="pl-9" data-testid="shop-search-input" />
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          {SHOP_CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`shop-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${cat === c ? "bg-primary text-white border-primary" : "border-border hover:bg-accent/40"}`}>
              {c}
            </button>
          ))}
          {courses?.some((c) => c.billing === "monthly") && (
            <button onClick={() => setCompare(true)} data-testid="shop-compare-memberships-button"
              className="px-3.5 py-1.5 rounded-full text-sm font-medium border border-primary text-primary hover:bg-primary/10 transition-colors sm:ml-auto">
              Compare memberships
            </button>
          )}
        </div>

        {!courses ? (
          <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : visible.length === 0 ? (
          <p className="text-center text-muted-foreground py-24">No courses in this category yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {visible.map((c) => (
              <Card key={c.id} className="overflow-hidden group border-border flex flex-col stagger-in" data-testid={`shop-course-${c.id}`}>
                <div className="relative h-40 bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  {c.thumbnail && <img src={c.thumbnail} alt={c.title} className="h-full w-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />}
                  <Badge className={`absolute top-3 left-3 border-0 text-white ${c.course_type === "live" ? "bg-red-500 hover:bg-red-500" : "bg-emerald-600 hover:bg-emerald-600"}`}>
                    {c.course_type === "live" ? <><Radio className="h-3 w-3 mr-1" />LIVE</> : <><GraduationCap className="h-3 w-3 mr-1" />CPD</>}
                  </Badge>
                  {c.free_for_members && <Badge className="absolute top-3 right-3 bg-amber-500 hover:bg-amber-500 text-white border-0">Free for members</Badge>}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`shop-course-categories-${c.id}`}>{cats(c).join(" · ")}</span>
                  <h3 className="font-heading font-semibold text-base leading-snug mt-1 line-clamp-2 min-h-[2.75rem]">{c.title}</h3>
                  {c.billing === "monthly" ? (
                    <div className="relative mt-1.5 flex-1">
                      <RichText html={c.description} className="text-sm text-muted-foreground max-h-40 overflow-hidden" testid={`shop-description-${c.id}`} />
                      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-card to-transparent" />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1.5 line-clamp-3 flex-1" data-testid={`shop-synopsis-${c.id}`}>{c.synopsis || stripHtml(c.description)}</p>
                  )}
                  {c.description && (
                    <button onClick={() => setDetails(c)} className={c.billing === "monthly" ? "text-xs font-medium text-primary hover:underline mt-2 self-start" : "mt-3 self-start inline-flex items-center gap-1.5 rounded-full border-2 border-primary bg-primary/5 px-3.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary hover:text-primary-foreground transition-colors"} data-testid={`shop-details-${c.id}-button`}>{c.billing === "monthly" ? "View details" : <><BookOpen className="h-3.5 w-3.5" />View course info</>}</button>
                  )}
                  <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.duration_hours}h</span>
                    {c.cpd_points > 0 && <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" />{c.cpd_points} CPD pts</span>}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    {c.register_interest ? (
                      <>
                        <span className="text-sm font-medium text-muted-foreground">Coming soon</span>
                        <Button size="sm" onClick={() => setInterest(c)} data-testid={`shop-interest-${c.id}-button`}>Register interest <ArrowRight className="h-4 w-4 ml-1" /></Button>
                      </>
                    ) : (
                      <>
                        <span className="font-heading text-2xl font-bold" data-testid={`shop-price-${c.id}`}>{Number(c.price) <= 0 ? "Free" : <>£{c.price}{c.billing === "monthly" && <span className="text-sm font-medium text-muted-foreground">/mo</span>}<span className="text-xs font-medium text-muted-foreground">{" "}ex VAT</span></>}</span>
                        <Button size="sm" onClick={() => setSelected(c)} data-testid={`shop-enrol-${c.id}-button`}>{c.billing === "monthly" ? "Subscribe" : "Enrol now"} <ArrowRight className="h-4 w-4 ml-1" /></Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="flex items-center justify-center gap-6 mt-12 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" />Secure Stripe checkout</span>
          <span className="flex items-center gap-1.5"><Video className="h-4 w-4 text-primary" />Instant access link</span>
          <span className="flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-amber-600" />Certificate on completion</span>
        </div>
      </main>

      {selected && <CheckoutDialog course={selected} onClose={() => setSelected(null)} />}
      {interest && <InterestDialog course={interest} onClose={() => setInterest(null)} />}
      {compare && <CompareMembershipsDialog onClose={() => setCompare(false)} onChoose={(id) => { setCompare(false); const c = courses.find((x) => x.id === id); if (c) setSelected(c); }} />}
      {details && (
        <Dialog open onOpenChange={() => setDetails(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="shop-details-dialog">
            <DialogHeader><DialogTitle className="font-heading">{details.title}</DialogTitle></DialogHeader>
            <RichText html={details.description} className="text-sm text-foreground/90" testid="shop-details-description" />
            <DialogFooter className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 bg-background border-t border-border">
              <Button variant="outline" onClick={() => setDetails(null)}>Close</Button>
              {!details.register_interest && (
                <Button onClick={() => { setSelected(details); setDetails(null); }} data-testid="shop-details-enrol-button">{details.billing === "monthly" ? "Subscribe" : "Enrol now"}</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function InterestDialog({ course, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!name.trim() || !email.trim()) return toast.error("Please enter your name and email");
    setLoading(true);
    try {
      await api.post("/shop/interest", { course_id: course.id, name, email });
      toast.success("Thanks! We've registered your interest and will be in touch.");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="interest-dialog">
        <DialogHeader><DialogTitle className="font-heading">Register interest: {course.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Leave your details and our team will contact you when this course is available.</p>
          <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="interest-name-input" /></div>
          <div><Label>Email address</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="interest-email-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="interest-submit-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Register interest"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckoutDialog({ course, onClose }) {
  const isMonthly = course.billing === "monthly";
  const isLive = course.course_type === "live" && !isMonthly;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState(isLive ? null : []);
  const [sessionId, setSessionId] = useState("");
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (isLive) {
      (async () => {
        try { const { data } = await api.get(`/shop/courses/${course.id}/sessions`); setSessions(data); }
        catch { setSessions([]); }
      })();
    }
  }, [course.id]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoInput, course_id: course.id });
      if (data.valid) { setPromo(data); toast.success(`Code applied — ${data.message}`); }
      else { setPromo(null); toast.error(data.message || "Invalid code"); }
    } catch { toast.error("Could not validate code"); }
    finally { setChecking(false); }
  };

  const clearPromo = () => { setPromo(null); setPromoInput(""); };

  const finalPrice = promo ? promo.final_price : course.price;
  const vat = Math.round(finalPrice * 20) / 100;
  const total = (Math.round((finalPrice + vat) * 100) / 100).toFixed(2);
  const [unlocking, setUnlocking] = useState(false);

  const emailMismatch = confirmEmail.trim() !== "" && confirmEmail.trim().toLowerCase() !== email.trim().toLowerCase();
  const emailsOk = () => {
    if (confirmEmail.trim().toLowerCase() === email.trim().toLowerCase()) return true;
    toast.error("Your email addresses don't match");
    return false;
  };

  const memberUnlock = async () => {
    if (!name || !email) return toast.error("Enter your name and email first");
    if (!emailsOk()) return;
    if (isLive && !sessionId) return toast.error("Please choose a session date");
    setUnlocking(true);
    try {
      const { data } = await api.post("/payments/member-unlock", {
        course_id: course.id, candidate_name: name, candidate_email: email,
        origin_url: window.location.origin, session_id: isLive ? sessionId : null,
      });
      toast.success("Unlocked! Check your email for the access link.");
      window.location.href = `/access/${data.access_token}`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setUnlocking(false);
    }
  };

  const addBasket = () => {
    if (!name || !email) return toast.error("Enter the learner's name and email");
    if (!emailsOk()) return;
    if (isLive && !sessionId) return toast.error("Please choose a session date");
    addToBasket(course, sessions.find((s) => s.id === sessionId), { name: name.trim(), email: email.trim() });
    toast.success("Added to basket", { action: { label: "View basket", onClick: () => { window.location.href = "/basket"; } } });
    onClose();
  };

  const checkout = async () => {
    if (!name || !email) return toast.error("Please enter your name and email");
    if (!emailsOk()) return;
    if (isLive && !sessionId) return toast.error("Please choose a session date");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        course_id: course.id, candidate_name: name, candidate_email: email,
        origin_url: window.location.origin,
        session_id: isLive ? sessionId : null,
        promo_code: promo ? promo.code : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Enrol in {course.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border p-4 bg-accent/30">
            <div>
              <Badge variant="outline" className={isLive ? "border-red-300 text-red-700" : "border-emerald-300 text-emerald-700"}>
                {isMonthly ? "Monthly subscription" : isLive ? "Live session" : "Self-paced CPD"}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1.5">{isMonthly ? "Billed monthly" : "Access link emailed after payment"}</p>
            </div>
            <div className="text-right">
              {promo && <p className="text-xs text-muted-foreground line-through">£{course.price}</p>}
              <span className="font-heading text-2xl font-bold">£{finalPrice}{isMonthly && <span className="text-sm font-medium text-muted-foreground">/mo</span>}</span>
              <p className="text-xs text-muted-foreground">ex VAT</p>
            </div>
          </div>
          <div className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid="checkout-vat-breakdown">
            <div className="flex justify-between"><span className="text-muted-foreground">Price (ex VAT)</span><span>£{Number(finalPrice).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">VAT (20%)</span><span data-testid="checkout-vat-amount">£{vat.toFixed(2)}</span></div>
            <div className="flex justify-between font-semibold pt-1 border-t border-border"><span>Total to pay{isMonthly && " per month"}</span><span data-testid="checkout-total-amount">£{total}</span></div>
          </div>

          {isLive && (
            <div>
              <Label>Choose your session date</Label>
              {sessions === null ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-1.5">No upcoming dates scheduled yet. Please check back soon.</p>
              ) : (
                <div className="space-y-2 mt-1.5">
                  {sessions.map((s) => (
                    <button type="button" key={s.id} onClick={() => setSessionId(s.id)} disabled={s.seats_left === 0}
                      data-testid={`checkout-session-${s.id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-colors disabled:opacity-50 ${sessionId === s.id ? "border-primary bg-accent/50" : "border-border hover:bg-accent/30"}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-primary" />{fmtDate(s.start)}</span>
                        {sessionId === s.id && <CheckCircle2 className="h-4 w-4 text-primary" />}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTime(s.start)}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.location}</span>
                        <span className="ml-auto">{s.seats_left > 0 ? `${s.seats_left} seats left` : "Full"}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div><Label>{isMonthly ? "Setting Name" : "Full Learners Name (for Certificate)"}</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={isMonthly ? "e.g. Little Stars Nursery" : "Jane Candidate"} className="mt-1.5" data-testid="checkout-name-input" /></div>
          <div><Label>{isMonthly ? "Setting email (for your member login)" : "Email address (for Course Links and Certificate)"}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5" data-testid="checkout-email-input" /><p className="text-xs text-muted-foreground mt-1">{isMonthly ? "We'll send your member login details here." : "We'll send your course access link here."}</p></div>
          <div>
            <Label>Confirm your email</Label>
            <Input type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} onPaste={(e) => e.preventDefault()} placeholder="Type your email again" className={`mt-1.5 ${emailMismatch ? "border-red-500 focus-visible:ring-red-500" : ""}`} data-testid="checkout-confirm-email-input" />
            {emailMismatch && <p className="text-xs text-red-600 mt-1" data-testid="checkout-email-mismatch">Email addresses don't match</p>}
          </div>

          <div>
            <Label>Discount code</Label>
            {promo ? (
              <div className="flex items-center justify-between mt-1.5 rounded-lg border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 p-3">
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5"><Tag className="h-4 w-4" />{promo.code} · {promo.message}</span>
                <button onClick={clearPromo} data-testid="remove-promo-button" className="text-emerald-700"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1.5">
                <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="promo-code-input" />
                <Button variant="outline" onClick={applyPromo} disabled={checking} data-testid="apply-promo-button">{checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
              </div>
            )}
          </div>

          {course.free_for_members && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-3" data-testid="member-unlock-panel">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Free for members</p>
              <p className="text-xs text-muted-foreground mt-0.5">On an active membership plan? Enter your details above{isLive ? " and pick a date" : ""}, then unlock this course free.</p>
              <Button variant="outline" size="sm" className="mt-2 w-full border-amber-400 text-amber-800 dark:text-amber-300" onClick={memberUnlock} disabled={unlocking} data-testid="member-unlock-button">
                {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock free as a member"}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!isMonthly && (
            <Button variant="outline" onClick={addBasket} data-testid="checkout-add-basket-button"><ShoppingBasket className="h-4 w-4 mr-1.5" />Add to basket</Button>
          )}
          <Button onClick={checkout} disabled={loading} data-testid="checkout-pay-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Number(total) <= 0 && !isMonthly ? "Get free access" : isMonthly ? `Subscribe £${total}/month inc VAT` : `Pay £${total} inc VAT`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
